import { createApi } from '@reduxjs/toolkit/query/react';
import { EventImageApiResponse } from 'domain/event';
import { EventSeries } from 'domain/event/EventSeries';
import {
  mapEventSeriesDetailsFromApi,
  mapEventSeriesToUpdateEventSeriesApiPayload,
  mapEventSeriesWizardFormValuesToApi,
} from 'event/mappers';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { generateFormData } from 'store/utils';

import { omitUndefined } from './utils';

import type {
  ConvertSingleEventToSeriesPayload,
  CreateEventPayload,
  CreateEventSeriesResponse,
  DeleteEventsFromSeriesArgs,
  UpdateEventPayload,
  UpdateEventSeriesImagePayload,
  UpdateEventSeriesQuestionsPayload,
} from './types';

export const eventSeriesApi = createApi({
  reducerPath: 'eventSeriesApi',
  baseQuery: axiosBaseQuery({ baseURL: getApiBaseUrl('v1') }),
  tagTypes: ['EVENT_SERIES'],
  endpoints: (builder) => ({
    getEventSeriesDetails: builder.query<EventSeries, { seriesId: number }>({
      keepUnusedDataFor: 0,
      query: ({ seriesId }) => ({
        url: `/eventSeries/${seriesId}`,
      }),
      transformResponse: mapEventSeriesDetailsFromApi,
      providesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    createEventSeries: builder.mutation<CreateEventSeriesResponse, CreateEventPayload>({
      query: (data) => ({
        url: '/eventSeries',
        method: 'POST',
        data: mapEventSeriesWizardFormValuesToApi(data),
      }),
    }),
    updateEventSeries: builder.mutation<void, UpdateEventPayload>({
      query: ({ seriesId, ...data }) => ({
        url: `/eventSeries/${seriesId}`,
        method: 'PUT',
        data: mapEventSeriesWizardFormValuesToApi(data),
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    convertSingleEventToSeries: builder.mutation<CreateEventSeriesResponse, ConvertSingleEventToSeriesPayload>({
      query: ({ id, ...data }) => ({
        url: `/eventSeries/withEvent/${id}`,
        method: 'POST',
        data: mapEventSeriesWizardFormValuesToApi(data),
      }),
      invalidatesTags: (result) => (result ? [{ type: 'EVENT_SERIES', id: result.id }] : []),
    }),
    deleteEventSeries: builder.mutation<void, { seriesId: number }>({
      query: ({ seriesId }) => ({
        url: `/eventSeries/${seriesId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    deleteEventsFromSeries: builder.mutation<void, DeleteEventsFromSeriesArgs>({
      query: ({ eventSeries, eventIdsToRemove }) => {
        if (eventSeries.events.length === eventIdsToRemove.length) {
          return {
            url: `/eventSeries/${eventSeries.id}`,
            method: 'DELETE',
          };
        }
        return {
          url: `/eventSeries/${eventSeries.id}`,
          method: 'PUT',
          data: mapEventSeriesToUpdateEventSeriesApiPayload({ data: eventSeries, eventIdsToRemove }),
        };
      },
      invalidatesTags: (_result, _error, { eventSeries }) => [{ type: 'EVENT_SERIES', id: eventSeries.id }],
    }),
    updateEventSeriesQuestions: builder.mutation<void, UpdateEventSeriesQuestionsPayload>({
      query: ({ seriesId, questions }) => ({
        url: `/eventSeries/${seriesId}/questions`,
        method: 'POST',
        data: questions,
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    postEventSeriesImage: builder.mutation<EventImageApiResponse, UpdateEventSeriesImagePayload>({
      query: ({ seriesId, ...params }) => ({
        url: `/eventSeries/${seriesId}/images`,
        headers: { 'Content-Type': 'multipart/form-data' },
        method: 'POST',
        data: generateFormData(omitUndefined(params)),
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
  }),
});

export const {
  useCreateEventSeriesMutation,
  useGetEventSeriesDetailsQuery,
  useDeleteEventsFromSeriesMutation,
  useUpdateEventSeriesQuestionsMutation,
} = eventSeriesApi;
